import { useState, type ReactNode } from 'react';
import {
	ArrowUpDown,
	Trash2,
	Tag,
	EyeOff,
	CheckSquare,
	Square,
	HandCoins,
	LayoutGrid,
	Store,
	ListFilter,
	BadgeDollarSign,
	Zap,
	Sparkles,
	CheckCircle2,
	Copy,
	Camera,
	MoreHorizontal
} from 'lucide-react';

import { EbayBag } from '@/components/icons/EbayBag';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ToolbarSearchInput } from '@/components/shared/ToolbarSearchInput';
import { InventoryViewToggle } from './InventoryViewToggle';
import { useTouchScrollDetection } from '@/hooks/useTouchScrollDetection';
import { cn } from '@/lib/utils';
import { toolbarControl } from '@/lib/buttonStyles';
import { CollectionSortOption } from '@/components/collection/CollectionSortButton';
import { ItemVisibility } from '@/types/userItem';
import type { SelectionSummary } from './SelectionActionBar';

interface InventoryToolbarProps {
	searchQuery: string;
	setSearchQuery: (value: string) => void;
	sortBy: CollectionSortOption;
	setSortBy: (value: CollectionSortOption) => void;
	onToggleFilters?: () => void;
	// Bulk actions props
	filteredCount?: number;
	pricedItemsCount?: number;
	onBulkAction?: (visibility: ItemVisibility) => Promise<void> | void;
	onBulkStockImages?: () => Promise<void>;
	onBulkPriceEdit?: () => void;
	/** Opens the desktop Rapid List queue for every unlisted item in the current filter view. */
	onRapidList?: () => void;
	/** Count of items that would be walked in Rapid List (drives disabled state + label). */
	rapidListCount?: number;
	onBulkPhotoUpload?: () => void;
	onEnterSelectionMode?: () => void;

	isBulkProcessing?: boolean;
	showBulkActions?: boolean;
	isSelectionMode?: boolean;
	// Selection mode props
	selectedCount?: number;
	totalCount?: number;
	onCancelSelection?: () => void;
	onDeleteSelected?: () => void;
	onListSelected?: () => void;
	onPrivateSelected?: () => void;
	onAcceptOffersSelected?: () => void;
	onSaveSelectedToMenu?: () => void;
	onListSelectedOnEbay?: () => void;
	onPriceFromCollectrSelected?: () => void;
	onSuggestPricesSelected?: () => void;
	onMarkSoldSelected?: () => void;
	onDuplicateSelected?: () => void;

	selectionSummary?: SelectionSummary;
	onSelectAll?: () => void;
	onDeselectAll?: () => void;
	// Storefront actions (consolidated into Options)
	username?: string | null;
	listedCount?: number;
	inventoryTotal?: number;
	visibility?: 'all' | 'listed' | 'unlisted';
	onVisibilityChange?: (value: 'all' | 'listed' | 'unlisted') => void;
}

const SORT_OPTIONS: { value: CollectionSortOption; label: string }[] = [
	{ value: 'priced_first', label: 'Priced First' },
	{ value: 'newest', label: 'Newest First' },
	{ value: 'oldest', label: 'Oldest First' },
	{ value: 'high_quantity', label: 'High Quantity First' },
	{ value: 'no_images', label: 'No Images First' },
	{ value: 'price_asc', label: 'Price: Low → High' },
	{ value: 'price_desc', label: 'Price: High → Low' },
	{ value: 'title_asc', label: 'Title: A to Z' }
];

export function InventoryToolbar({
	searchQuery,
	setSearchQuery,
	sortBy,
	setSortBy,
	onToggleFilters,
	filteredCount = 0,
	pricedItemsCount = 0,
	onBulkAction,
	onBulkStockImages,
	onBulkPriceEdit,
	onRapidList,
	rapidListCount = 0,
	onBulkPhotoUpload,
	onEnterSelectionMode,

	isBulkProcessing = false,
	showBulkActions = false,
	isSelectionMode = false,
	selectedCount = 0,
	totalCount = 0,
	onCancelSelection,
	onDeleteSelected,
	onListSelected,
	onPrivateSelected,
	onAcceptOffersSelected,
	onSaveSelectedToMenu,
	onListSelectedOnEbay,
	onPriceFromCollectrSelected,
	onSuggestPricesSelected,
	onMarkSoldSelected,
	onDuplicateSelected,

	selectionSummary,
	onSelectAll,
	onDeselectAll,
	username,
	listedCount,
	inventoryTotal,
	visibility = 'all',
	onVisibilityChange
}: InventoryToolbarProps) {
	const [sortOpen, setSortOpen] = useState(false);
	const [moreOpen, setMoreOpen] = useState(false);
	const sortTouchHandlers = useTouchScrollDetection(setSortOpen);

	// Shared surface class for all toolbar controls - force override of Button(variant="outline") bg-transparent
	const toolbarSurface =
		'!bg-[hsl(var(--toolbar-control-bg,var(--background)))] hover:!bg-[hsl(var(--toolbar-control-bg,var(--background)))]';

	// Selection mode toolbar
	if (isSelectionMode) {
		const allSelected = selectedCount > 0 && selectedCount === totalCount;
		const hasSelection = selectedCount > 0;

		const summary = selectionSummary ?? {
			total: selectedCount,
			listed: 0,
			unlisted: selectedCount,
			pricedUnlisted: 0,
			acceptingOffers: 0
		};
		const allListed = hasSelection && summary.listed === summary.total;
		const allUnlisted = hasSelection && summary.unlisted === summary.total;
		const isMixed = hasSelection && !allListed && !allUnlisted;
		const showList = hasSelection && !allListed;
		const showUnlist = hasSelection && !allUnlisted;
		const offersMissing = Math.max(0, summary.total - summary.acceptingOffers);
		const showOffers = hasSelection && !!onAcceptOffersSelected && offersMissing > 0;
		const listLabel = isMixed ? `List ${summary.pricedUnlisted || summary.unlisted}` : 'List';
		const unlistLabel = isMixed ? `Unlist ${summary.listed}` : 'Unlist';
		const offersLabel = offersMissing < summary.total ? `Offers ${offersMissing}` : 'Offers';

		const secondaryMenuItems: {
			key: string;
			label: string;
			icon: ReactNode;
			onClick: () => void;
			disabled: boolean;
		}[] = [];
		if (onSuggestPricesSelected && hasSelection) {
			secondaryMenuItems.push({
				key: 'suggest',
				label: 'Suggest prices',
				icon: <Sparkles className="h-4 w-4 text-active-gold" />,
				onClick: onSuggestPricesSelected,
				disabled: isBulkProcessing
			});
		}
		if (!!onPriceFromCollectrSelected && (selectionSummary?.collectrPriceable ?? 0) > 0) {
			secondaryMenuItems.push({
				key: 'collectr',
				label: 'Collectr Price',
				icon: <BadgeDollarSign className="h-4 w-4 text-active-gold" />,
				onClick: onPriceFromCollectrSelected,
				disabled: isBulkProcessing
			});
		}
		if (onDuplicateSelected && hasSelection) {
			secondaryMenuItems.push({
				key: 'duplicate',
				label: 'Duplicate',
				icon: <Copy className="h-4 w-4 text-active-gold" />,
				onClick: onDuplicateSelected,
				disabled: isBulkProcessing
			});
		}
		if (onMarkSoldSelected && hasSelection) {
			secondaryMenuItems.push({
				key: 'sold',
				label: 'Sold',
				icon: <CheckCircle2 className="h-4 w-4 text-active-gold" />,
				onClick: onMarkSoldSelected,
				disabled: isBulkProcessing
			});
		}
		if (onSaveSelectedToMenu && hasSelection) {
			secondaryMenuItems.push({
				key: 'make-menu',
				label: 'Make Menu',
				icon: <LayoutGrid className="h-4 w-4 text-active-gold" />,
				onClick: onSaveSelectedToMenu,
				disabled: false
			});
		}
		if (onListSelectedOnEbay && hasSelection) {
			secondaryMenuItems.push({
				key: 'ebay',
				label: 'eBay',
				icon: <EbayBag className="h-4 w-4 text-active-gold" />,
				onClick: onListSelectedOnEbay,
				disabled: false
			});
		}

		return (
			<div className="flex items-center gap-2">
				{/* Tap-to-toggle count chip — matches toolbar control surface */}
				<button
					type="button"
					onClick={allSelected ? onDeselectAll : onSelectAll}
					className={cn(
						'h-10 px-3 shrink-0 rounded-full border border-input transition-all duration-smooth',
						toolbarSurface,
						'hover:border-white/20 focus:border-white/30 active:border-white/40',
						'flex items-center gap-2'
					)}
					title={allSelected ? 'Deselect all' : 'Select all'}
				>
					{allSelected ? (
						<CheckSquare className="h-4 w-4 text-active-gold" />
					) : (
						<Square className="h-4 w-4 text-muted-foreground" />
					)}
					<span className="text-sm font-medium tabular-nums">
						{selectedCount}
						<span className="text-muted-foreground font-normal"> / {totalCount}</span>
					</span>
				</button>

				<div className="flex-1" />

				{/* Primary state-aware actions */}
				{showList && (
					<Button
						onClick={onListSelected}
						variant="outline"
						disabled={!hasSelection || summary.pricedUnlisted === 0}
						className={cn(
							'h-10 px-4 shrink-0 gap-2 transition-all duration-smooth',
							toolbarSurface,
							'hover:border-white/20 focus:border-white/30',
							toolbarControl
						)}
					>
						<Tag className="h-4 w-4 text-active-gold" />
						<span className="text-sm font-medium">{listLabel}</span>
					</Button>
				)}
				{showOffers && (
					<Button
						onClick={onAcceptOffersSelected}
						variant="outline"
						disabled={!hasSelection || isBulkProcessing}
						className={cn(
							'h-10 px-4 shrink-0 gap-2 transition-all duration-smooth',
							toolbarSurface,
							'hover:border-white/20 focus:border-white/30',
							toolbarControl
						)}
						title="Turn on accepting offers"
					>
						<HandCoins className="h-4 w-4 text-active-gold" />
						<span className="text-sm font-medium">{offersLabel}</span>
					</Button>
				)}
				{showUnlist && (
					<Button
						onClick={onPrivateSelected}
						variant="outline"
						disabled={!hasSelection}
						className={cn(
							'h-10 px-4 shrink-0 gap-2 transition-all duration-smooth',
							toolbarSurface,
							'hover:border-white/20 focus:border-white/30',
							toolbarControl
						)}
					>
						<EyeOff className="h-4 w-4 text-active-gold" />
						<span className="text-sm font-medium">{unlistLabel}</span>
					</Button>
				)}

				{/* Secondary actions collapsed into a single menu */}
				{secondaryMenuItems.length > 0 && (
					<DropdownMenu open={moreOpen} onOpenChange={setMoreOpen}>
						<DropdownMenuTrigger asChild>
							<Button
								variant="outline"
								className={cn(
									'h-10 px-4 shrink-0 gap-2 transition-all duration-smooth',
									toolbarSurface,
									'hover:border-white/20 focus:border-white/30 active:border-white/40',
									toolbarControl,
									moreOpen && 'border-white/40'
								)}
							>
								<MoreHorizontal className="h-4 w-4" />
								<span className="text-sm font-medium">More</span>
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="w-48 z-[60]">
							{secondaryMenuItems.map((item) => (
								<DropdownMenuItem
									key={item.key}
									onClick={item.onClick}
									disabled={item.disabled}
									className={cn('gap-2', item.disabled && 'opacity-50 cursor-not-allowed')}
								>
									{item.icon}
									<span className="text-sm font-medium">{item.label}</span>
								</DropdownMenuItem>
							))}
						</DropdownMenuContent>
					</DropdownMenu>
				)}

				{/* Delete — toolbar surface w/ destructive text */}
				<Button
					onClick={onDeleteSelected}
					variant="outline"
					disabled={!hasSelection}
					className={cn(
						'h-10 px-4 shrink-0 gap-2 transition-all duration-smooth',
						toolbarSurface,
						'hover:border-destructive/40 focus:border-destructive/40',
						toolbarControl,
						'text-destructive hover:text-destructive'
					)}
				>
					<Trash2 className="h-4 w-4" />
					<span className="text-sm font-medium">Delete</span>
				</Button>

				{/* Done — toolbar surface, standard text */}
				<Button
					onClick={onCancelSelection}
					variant="outline"
					className={cn(
						'h-10 px-4 shrink-0 transition-all duration-smooth',
						toolbarSurface,
						'hover:border-white/20 focus:border-white/30',
						toolbarControl
					)}
				>
					Done
				</Button>
			</div>
		);
	}

	const searchRightHint =
		inventoryTotal && inventoryTotal > 0 && listedCount !== undefined
			? `${listedCount}/${inventoryTotal} listed`
			: undefined;

	return (
		<div className="flex items-center gap-2">
			{/* Search — leftmost, takes remaining width */}
			<ToolbarSearchInput
				value={searchQuery}
				onValueChange={setSearchQuery}
				placeholder={
					filteredCount > 0
						? `Search ${filteredCount} item${filteredCount === 1 ? '' : 's'}…`
						: inventoryTotal && inventoryTotal > 0
							? `Search ${inventoryTotal} item${inventoryTotal === 1 ? '' : 's'}…`
							: 'Search…'
				}
				rightHint={searchRightHint}
				className="flex-1 min-w-[120px]"
			/>

			{/* Sort Dropdown */}
			<DropdownMenu open={sortOpen} onOpenChange={setSortOpen}>
				<DropdownMenuTrigger asChild>
					<Button
						variant="outline"
						{...sortTouchHandlers}
						className={cn(
							'h-10 px-4 gap-2 shrink-0 transition-all duration-smooth',
							toolbarSurface,
							'hover:border-white/20 focus:border-white/30 active:border-white/40',
							toolbarControl,
							sortOpen && 'border-white/40'
						)}
					>
						<ArrowUpDown className="w-4 h-4" />
						<span className="text-sm font-medium">Sort</span>
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="w-56 z-[60]">
					{SORT_OPTIONS.map((option) => (
						<DropdownMenuItem
							key={option.value}
							onClick={() => setSortBy(option.value)}
							className={cn('gap-2', sortBy === option.value && 'bg-accent text-accent-foreground')}
						>
							{option.label}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>

			{/* Listed / Unlisted quick toggle */}
			{onVisibilityChange && (
				<Button
					variant="outline"
					onClick={() => {
						const next: Record<typeof visibility, typeof visibility> = {
							all: 'listed',
							listed: 'unlisted',
							unlisted: 'all'
						};
						onVisibilityChange(next[visibility]);
					}}
					className={cn(
						'h-10 px-4 gap-2 shrink-0 transition-all duration-smooth',
						toolbarSurface,
						'hover:border-white/20 focus:border-white/30 active:border-white/40',
						toolbarControl,
						visibility !== 'all' &&
							'bg-active-gold/10 text-active-gold border-active-gold/30 hover:border-active-gold/40'
					)}
				>
					<ListFilter className="w-4 h-4" />
					<span className="text-sm font-medium">
						{visibility === 'all' ? 'All' : visibility === 'listed' ? 'Listed' : 'Unlisted'}
					</span>
				</Button>
			)}

			{/* Rapid List (desktop only, non-selection) */}
			{onRapidList && showBulkActions && (
				<Button
					variant="outline"
					onClick={onRapidList}
					disabled={rapidListCount === 0 || isBulkProcessing}
					title={
						rapidListCount === 0
							? 'Nothing to list — every item in view is already listed'
							: `Rapid-list ${rapidListCount} unlisted item${rapidListCount === 1 ? '' : 's'}`
					}
					className={cn(
						'hidden lg:flex h-10 px-4 gap-2 shrink-0 transition-all duration-smooth',
						toolbarSurface,
						'hover:border-active-gold/40 focus:border-active-gold/40',
						toolbarControl
					)}
				>
					<Zap className="w-4 h-4 text-active-gold" />
					<span className="text-sm font-medium">
						Bulk list{rapidListCount > 0 ? ` (${rapidListCount})` : ''}
					</span>
				</Button>
			)}

			{/* Rapid Photograph (desktop only, non-selection) */}
			{onBulkPhotoUpload && showBulkActions && (
				<Button
					variant="outline"
					onClick={onBulkPhotoUpload}
					disabled={isBulkProcessing}
					title="Queue through photoless items and capture front + back"
					className={cn(
						'hidden lg:flex h-10 px-4 gap-2 shrink-0 transition-all duration-smooth',
						toolbarSurface,
						'hover:border-active-gold/40 focus:border-active-gold/40',
						toolbarControl
					)}
				>
					<Camera className="w-4 h-4 text-active-gold" />
					<span className="text-sm font-medium">Bulk photograph</span>
				</Button>
			)}

			{/* View Mode Toggle */}
			<InventoryViewToggle />
		</div>
	);
}
